import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where
} from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../../firebase';
import { CompanyInfo } from '../../types';
import { sanitizeForFirestore } from '../common';

// Helper to generate random id if needed
function generateRandomId(): string {
  return Math.random().toString(36).substring(2, 10);
}

export async function getCompany(id: string): Promise<CompanyInfo | null> {
  const path = `companies/${id}`;
  try {
    const d = await getDoc(doc(db, 'companies', id));
    if (d.exists()) {
      return { id: d.id, ...d.data() } as CompanyInfo;
    }
    return null;
  } catch (err) {
    return handleFirestoreError(err, OperationType.GET, path);
  }
}

export async function updateCompany(id: string, info: Partial<CompanyInfo>): Promise<void> {
  const path = `companies/${id}`;
  try {
    const cleanInfo = sanitizeForFirestore(info);
    await setDoc(doc(db, 'companies', id), cleanInfo as any, { merge: true });
  } catch (err) {
    return handleFirestoreError(err, OperationType.UPDATE, path);
  }
}

// Helper to determine if company has an active paying subscription
export function parseCompanySubscriptionStatus(data: any): boolean {
  if (!data) return false;
  const sub = data?.subscription;
  const status = sub?.status;
  const isCanceled = status === 'canceled' || status === 'cancelled';
  const planId = (sub?.planId || '').toLowerCase();
  const planName = (sub?.planName || '').toLowerCase();
  
  const isNonSubPlan = isCanceled || planId === 'free_trial' || planId === 'free' || planId === 'test' || planId === 'credit_package' || planName.includes('kvik go') || planName.includes('prøve') || !planId;
  
  return !isNonSubPlan && status !== 'canceled' && status !== 'cancelled';
}

export interface CompanyRetentionInfo {
  hasActiveSub: boolean;
  downgradedToKvikGoAt?: string | null;
}

export function parseCompanyRetentionInfo(data: any): CompanyRetentionInfo {
  if (!data) return { hasActiveSub: false, downgradedToKvikGoAt: null };
  const hasActiveSub = parseCompanySubscriptionStatus(data);
  const sub = data?.subscription;
  const downgradedToKvikGoAt = 
    data?.downgradedToKvikGoAt || 
    sub?.downgradedToKvikGoAt || 
    sub?.canceledAt || 
    (sub?.status === 'canceled' ? (sub?.updatedAt || sub?.createdAt) : null);

  return {
    hasActiveSub,
    downgradedToKvikGoAt: downgradedToKvikGoAt || null,
  };
}

// Cache & in-flight promise map for company subscription checks to prevent excessive Firestore reads and N+1 roundtrips
const companySubStatusCache = new Map<string, { hasActiveSub: boolean; downgradedToKvikGoAt?: string | null; fetchedAt: number }>();
const companySubStatusInFlight = new Map<string, Promise<boolean>>();
const companyRetentionInFlight = new Map<string, Promise<CompanyRetentionInfo>>();
const COMPANY_SUB_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

export function invalidateCompanySubCache(companyId: string) {
  companySubStatusCache.delete(companyId);
  companySubStatusInFlight.delete(companyId);
  companyRetentionInFlight.delete(companyId);
}

// Batch prefetch company subscription statuses in chunks of 30 using where(documentId(), 'in', chunk)
export async function batchPrefetchCompanySubscriptions(companyIds: string[]): Promise<void> {
  const neededIds = Array.from(
    new Set(
      companyIds.filter(id => {
        if (!id || typeof id !== 'string') return false;
        const cached = companySubStatusCache.get(id);
        return !cached || (Date.now() - cached.fetchedAt >= COMPANY_SUB_CACHE_TTL_MS);
      })
    )
  );

  if (neededIds.length === 0) return;

  // Process in chunks of 30 (Firestore 'in' query limit)
  const chunkSize = 30;
  for (let i = 0; i < neededIds.length; i += chunkSize) {
    const chunk = neededIds.slice(i, i + chunkSize);
    try {
      if (chunk.length === 1) {
        const snap = await getDoc(doc(db, 'companies', chunk[0]));
        const retInfo = snap.exists() ? parseCompanyRetentionInfo(snap.data()) : { hasActiveSub: false, downgradedToKvikGoAt: null };
        companySubStatusCache.set(chunk[0], { ...retInfo, fetchedAt: Date.now() });
      } else {
        const q = query(collection(db, 'companies'), where(documentId(), 'in', chunk));
        const snap = await getDocs(q);
        const foundIds = new Set<string>();
        snap.docs.forEach(d => {
          foundIds.add(d.id);
          const retInfo = parseCompanyRetentionInfo(d.data());
          companySubStatusCache.set(d.id, {
            ...retInfo,
            fetchedAt: Date.now()
          });
        });
        // Cache missing companies as false
        chunk.forEach(id => {
          if (!foundIds.has(id)) {
            companySubStatusCache.set(id, { hasActiveSub: false, downgradedToKvikGoAt: null, fetchedAt: Date.now() });
          }
        });
      }
    } catch (err) {
      console.warn("Could not batch prefetch company subscription statuses:", err);
    }
  }
}

export async function getCompanyRetentionInfo(companyId: string): Promise<CompanyRetentionInfo> {
  if (!companyId) return { hasActiveSub: false, downgradedToKvikGoAt: null };
  const cached = companySubStatusCache.get(companyId);
  if (cached && (Date.now() - cached.fetchedAt < COMPANY_SUB_CACHE_TTL_MS)) {
    return { hasActiveSub: cached.hasActiveSub, downgradedToKvikGoAt: cached.downgradedToKvikGoAt };
  }

  if (companyRetentionInFlight.has(companyId)) {
    return companyRetentionInFlight.get(companyId)!;
  }

  const fetchPromise = (async () => {
    try {
      const snap = await getDoc(doc(db, 'companies', companyId));
      const retInfo = snap.exists() ? parseCompanyRetentionInfo(snap.data()) : { hasActiveSub: false, downgradedToKvikGoAt: null };
      companySubStatusCache.set(companyId, { ...retInfo, fetchedAt: Date.now() });
      return retInfo;
    } catch (err) {
      console.warn("Could not fetch company retention info:", err);
      return { hasActiveSub: false, downgradedToKvikGoAt: null };
    } finally {
      companyRetentionInFlight.delete(companyId);
    }
  })();

  companyRetentionInFlight.set(companyId, fetchPromise);
  return fetchPromise;
}

export async function isCompanySubscribed(companyId: string): Promise<boolean> {
  const info = await getCompanyRetentionInfo(companyId);
  return info.hasActiveSub;
}

export async function superGetAllCompanies(): Promise<CompanyInfo[]> {
  try {
    const snap = await getDocs(collection(db, 'companies'));
    const companiesList: CompanyInfo[] = [];
    snap.forEach(c => {
      companiesList.push({ id: c.id, ...c.data() as any } as CompanyInfo);
    });
    return companiesList;
  } catch (err) {
    return handleFirestoreError(err, OperationType.LIST, 'companies');
  }
}

export async function superAddCompany(name: string, cvr: string): Promise<void> {
  const id = `company-${name.toLowerCase().replace(/[^a-z0-9]/g, '') || generateRandomId()}`;
  try {
    await setDoc(doc(db, 'companies', id), {
      name,
      cvr,
      billingEmail: `faktura@${name.toLowerCase().replace(/[^a-z0-9]/g, '') || 'company'}.dk`,
      address: 'Hovedgaden 1',
      zip: '1000',
      city: 'København',
      country: 'Danmark',
      createdAt: new Date().toISOString()
    });
  } catch (err) {
    return handleFirestoreError(err, OperationType.CREATE, `companies/${id}`);
  }
}

export async function superDeleteCompany(companyId: string): Promise<{ success: boolean; message: string; stats?: any }> {
  const { apiRequest } = await import('../apiClient');
  return apiRequest<{ success: boolean; message: string; stats?: any }>(`/superadmin/companies/${encodeURIComponent(companyId)}`, {
    method: 'DELETE'
  });
}

